import React, { useState, useEffect, useMemo } from "react";
import { SubmitButton, UploadBox } from "../../components/Input";
import { API_URL } from "../../config";
import axios from "axios";
import { showToast } from "../../components/toast";
import CompanyHeader from "./CompanyHeader";

// Helper function to extract YouTube embed URL from various YouTube formats
const getYouTubeEmbedUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();

  // If already an embed URL
  if (trimmed.includes("youtube.com/embed/")) {
    const cleanUrl = trimmed.split("?")[0].split("&")[0];
    const videoId = cleanUrl.split("youtube.com/embed/")[1]?.replace(/[^a-zA-Z0-9_-]/g, "");
    return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
  }

  // Standard regex to match various YouTube URLs (watch?v=, youtu.be/, shorts/, etc.)
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|watch\?.+&v=)([^#&?]*).*/;
  const match = trimmed.match(regExp);

  if (match && match[2] && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}`;
  }

  // If user enters direct 11-char video id
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
    return `https://www.youtube.com/embed/${trimmed}`;
  }

  return "";
};

const PhilosophyVideoForm = () => {
  const [formData, setFormData] = useState({
    id: 1,
    philosophy_video: "",
    philosophy_thumbnail: "",
  });

  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("authToken");

  const fetchCompany = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}settings/company/company-info`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.data && response.data.data) {
        const data = response.data.data;
        setFormData({
          id: data.id || 1,
          philosophy_video: data.philosophy_video || "",
          philosophy_thumbnail: data.philosophy_thumbnail || "",
        });
        if (data.philosophy_thumbnail) setThumbPreview(data.philosophy_thumbnail);
      }
    } catch (error) {
      console.error("Error fetching company details:", error);
      showToast.error("Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, []);

  const handleVideoUrlChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      philosophy_video: e.target.value,
    }));
  };

  const handleThumbChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbFile(file);
      setThumbPreview(URL.createObjectURL(file));
    }
  };

  const youtubeEmbedUrl = useMemo(() => {
    return getYouTubeEmbedUrl(formData.philosophy_video);
  }, [formData.philosophy_video]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    setSaving(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("philosophy_video", formData.philosophy_video ? formData.philosophy_video.trim() : "");

      if (thumbFile) {
        formDataToSend.append("philosophy_thumbnail", thumbFile);
      }

      const response = await axios.put(
        `${API_URL}settings/company/update`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data && response.data.success) {
        showToast.success(response.data.message || "Philosophy Video & Thumbnail updated successfully");
        setThumbFile(null);
        fetchCompany();
      } else {
        showToast.error(response.data?.message || "Failed to update philosophy video");
      }
    } catch (error) {
      console.error("Error updating philosophy video:", error);
      showToast.error(error.response?.data?.message || "An error occurred while updating settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <CompanyHeader />

        <div className="panel panel-default with-rounded-corner with-shadow mt-3">
          <form onSubmit={handleSubmit} className="stdform" encType="multipart/form-data">
            <div className="panel-heading border-0 mb-3">
              <span className="panel-title font-weight-bold">
                Philosophy Video & Thumbnail Settings
              </span>
            </div>

            <div className="panel-body">
              {loading ? (
                <div className="text-center py-5">
                  <i className="fa fa-spinner fa-spin fa-2x"></i>
                  <p className="mt-2">Loading...</p>
                </div>
              ) : (
                <>
                  <div className="row">
                    {/* YouTube Video URL Field */}
                    <div className="col-md-6 mb-3">
                      <div className="form-group mb-2">
                        <label className="control-label font-weight-bold">
                          Philosophy Video (YouTube URL)
                        </label>
                        <div className="input-group">
                          <span className="input-group-addon" style={{ background: "#f8f9fa", borderColor: "#ced4da" }}>
                            <i className="fa fa-youtube-play text-danger" style={{ fontSize: "16px" }}></i>
                          </span>
                          <input
                            type="text"
                            name="philosophy_video"
                            className="form-control"
                            placeholder="e.g. https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                            value={formData.philosophy_video || ""}
                            onChange={handleVideoUrlChange}
                          />
                        </div>
                        <small className="form-text text-muted">
                          Enter any standard YouTube link, short link (youtu.be), or video ID.
                        </small>
                      </div>

                      {/* YouTube Video Preview Showcase */}
                      <div className="mt-3 p-3 bg-light rounded border">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="font-weight-bold m-0">
                            <i className="fa fa-play-circle mr-1 text-danger"></i> YouTube Video Preview
                          </label>
                          {formData.philosophy_video && (
                            <a
                              href={formData.philosophy_video}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="small text-primary"
                            >
                              Open in YouTube <i className="fa fa-external-link"></i>
                            </a>
                          )}
                        </div>

                        {youtubeEmbedUrl ? (
                          <div
                            style={{
                              position: "relative",
                              width: "100%",
                              paddingBottom: "56.25%", /* 16:9 Aspect Ratio */
                              height: 0,
                              overflow: "hidden",
                              borderRadius: "6px",
                              backgroundColor: "#000",
                            }}
                          >
                            <iframe
                              src={youtubeEmbedUrl}
                              title="Philosophy Video Preview"
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                border: 0,
                              }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                            ></iframe>
                          </div>
                        ) : (
                          <div
                            className="text-center text-muted p-4 border rounded"
                            style={{
                              backgroundColor: "#fff",
                              borderStyle: "dashed !important",
                              minHeight: "180px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <i className="fa fa-youtube-play fa-3x text-muted mb-2" style={{ opacity: 0.4 }}></i>
                            <p className="mb-0 small">
                              {formData.philosophy_video
                                ? "Invalid YouTube URL. Please provide a valid YouTube link to preview."
                                : "Enter a YouTube URL above to preview the video here."}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Upload Thumbnail Field */}
                    <div className="col-md-6 mb-3">
                      <UploadBox
                        label="Upload Philosophy Thumbnail"
                        fieldname="philosophy_thumbnail"
                        onChange={handleThumbChange}
                        accept="image/*"
                      />

                      {/* Philosophy Thumbnail Showcase */}
                      <div className="mt-3 p-3 bg-light rounded border">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="font-weight-bold m-0">
                            <i className="fa fa-image mr-1 text-info"></i> Philosophy Thumbnail Preview
                          </label>
                          {thumbPreview && (
                            <a
                              href={thumbPreview}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="small text-primary"
                            >
                              View Full Size <i className="fa fa-external-link"></i>
                            </a>
                          )}
                        </div>

                        {thumbPreview ? (
                          <div
                            style={{
                              background: "#f1f5f9",
                              borderRadius: "6px",
                              padding: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: "200px",
                              maxHeight: "260px",
                              overflow: "hidden",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <img
                              src={thumbPreview}
                              alt="Philosophy Thumbnail Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "240px",
                                objectFit: "contain",
                                borderRadius: "4px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="text-center text-muted p-4 border rounded"
                            style={{
                              backgroundColor: "#fff",
                              borderStyle: "dashed !important",
                              minHeight: "180px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <i className="fa fa-picture-o fa-3x text-muted mb-2" style={{ opacity: 0.4 }}></i>
                            <p className="mb-0 small">No thumbnail uploaded yet. Choose an image file above to preview.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="clearfix"></div>

                  <div className="mt-4 pt-3 border-top">
                    {saving ? (
                      <button type="button" className="btn btn-primary" disabled>
                        <i className="fa fa-spinner fa-spin mr-5"></i>Saving...
                      </button>
                    ) : (
                      <SubmitButton pageBack="/Settings/Company" handleSubmit={handleSubmit} />
                    )}
                  </div>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PhilosophyVideoForm;
